import { ODataComparer } from './odata-comparer';
import { ODataExpand } from './odata-expand';
import { ODataFilter } from './odata-filter';
import { ODataOrder } from './odata-order';
import { ODataTop } from './odata-top';
import { ODataSkip } from './odata-skip';
import { ODataSelect } from './odata-select';

//TODO:  Build in support for $select, $format, $inlinecount, and custom functions
/**
 * A convenience utility for composing an OData query string in a more readable 
 * way.  All public methods (except toString) return the ODataBuilder so methods can
 * easily be chained.
 * 
 * Usage:
 *   //Initialize the ODataBuilder with the base URL you are calling
 *   let oDataBuilder:ODataBuilder = new ODataBuilder("https://localhost/v1/ClubGoals");
 *
 *   //Compose the query in method format.
 *   oDataBuilder
 *    .filter("ClubId", ODataComparer.Equals, clubGoal.ClubId)
 *    .filter("GoalId", ODataComparer.Equals, clubGoal.GoalId)
 *    .filter("FiscalYear/StartDate", ODataComparer.LessThan, selectedFiscalYear.StartDate)
 *    .expand("FiscalYear")
 *    .orderBy("FiscalYear/StartDate", true);
 *
 *  //Call toString to return the result URL.
 *  let clubGoalUrl:string = oDataBuilder.toString();
 */
export class ODataBuilder {

    private filters:ODataFilter[] = [];
    private expands:ODataExpand[] = [];
    private orderBys:ODataOrder[] = [];
    private topValue:ODataTop = null;
    private skipValue:ODataSkip = null;
    private selectValue:ODataSelect = null;
    
    constructor(private baseUrl:string) {
        
    }

    //Only supports "And" filters for now.  "Or" would be possible, but more complicated and less common
    /**
     * Adds a filter clause to the OData query string.  Reduces the result
     * set returned from the server based on the expression passed in.  Multiple
     * filters can be added further reducing the result set.
     * 
     * @param {string} property - The property name to filter on.
     * @param {ODataComparer} comparer - an Enum representing the filter's comparison method
     * @param {any} value - The value to compare the propery against
     * 
     * @return {ODataBuilder} - Returns the ODataBuilder in it's
     * current state for chaining other calls
     */
    public filter(property:string, comparer:ODataComparer, value:any):ODataBuilder{
        this.filters.push(new ODataFilter(property, comparer, value));
        return this;
    }

    /**
     * Adds an expand clause to the OData query string.  Ensures
     * that navigation properties are included in the result set.
     * Multiple expands can be added causing more than one navigation
     * property to be included.
     * 
     * @param {string} property - The property name to expand.
     * 
     * @return {ODataBuilder} - Returns the ODataBuilder in it's
     * current state for chaining other calls
     */
    public expand(property:string):ODataBuilder{
        this.expands.push(new ODataExpand(property));
        return this;
    }

    /**
     * Adds a orderby clause to the OData query string.  Sorts
     * the result set in the manner specified by the parameters.
     * Multiple orderBys can be added to refine the sort order.
     * 
     * @param {string} property - The property name to sort by.
     * @param {boolean} isDecending - Specifies the sort direction.  Default is "false"
     * 
     * @return {ODataBuilder} - Returns the ODataBuilder in it's
     * current state for chaining other calls
     */
    public orderBy(property:string, isDecending:boolean = false):ODataBuilder{
        this.orderBys.push(new ODataOrder(property, isDecending));
        return this;
    }

    /**
     * Adds a top clause to the OData query.  Causes the
     * query to return the number of results specifed in the
     * value parameter starting from the beginning of the
     * result set.  If top is called multiple times, only the value specified 
     * in the final call is used.
     * 
     * @param {number} value - Number of results to return
     * 
     * @return {ODataBuilder} - Returns the ODataBuilder in it's
     * current state for chaining other calls
     */
    public top(value:number):ODataBuilder{
        this.topValue = new ODataTop(value);
        return this;
    }
    
    /**
     * Adds a skip clause to the OData query.  Causes the
     * query to skip over the number of results specified in 
     * the value parameter and return the remaining results. 
     * If skip is called multiple times, only the value specified 
     * in the final call is used.
     * 
     * @param {number} value - Number of results to skip over
     * 
     * @return {ODataBuilder} - Returns the ODataBuilder in it's
     * current state for chaining other calls
     */
    public skip(value:number):ODataBuilder{
        this.skipValue = new ODataSkip(value);
        return this;
    }

    /**
     * Adds a select clause to the OData query.  Causes the
     * query to return a subset of the properties included
     * in response.
     * 
     * @param {Array<string>} properties - Array of properties
     * to include
     * 
     * @return {ODataBuilder} - Returns the ODataBuilder in it's
     * current state for chaining other calls
     */
    public select(value:Array<string>):ODataBuilder{
        this.selectValue = new ODataSelect(value);
        return this;
    }

    /**
     * Returns a URL with an OData query string that represents the 
     * filters, expands, orderBys, tops, and skips passed into the
     * ODataBuilder.
     * 
     * @return {string} - The resulting OData URL
     */
    public toString():string{
        let isQueryStringIndicatorAdded:boolean = false;
        let url:string = this.baseUrl;

        if(this.filters.length != 0){
            let filterString:string = this.getFilterString(isQueryStringIndicatorAdded);
            url += filterString;
            isQueryStringIndicatorAdded = true;
        }

        if(this.expands.length != 0){
            let expandString:string = this.getExpandString(isQueryStringIndicatorAdded);
            url += expandString;
            isQueryStringIndicatorAdded = true;
        }

        if(this.orderBys.length != 0){
            let orderByString:string = this.getOrderByString(isQueryStringIndicatorAdded);
            url += orderByString;
            isQueryStringIndicatorAdded = true;
        }

        if(this.topValue != null){
            let topString:string = this.getTopString(isQueryStringIndicatorAdded);
            url += topString;
            isQueryStringIndicatorAdded = true;
        }

        if(this.skipValue != null){
            let skipString:string = this.getSkipString(isQueryStringIndicatorAdded);
            url += skipString;
            isQueryStringIndicatorAdded = true;
        }

        if(this.selectValue != null){
            let selectString:string = this.getSelectString(isQueryStringIndicatorAdded);
            url += selectString;
            isQueryStringIndicatorAdded = true;
        }

        return url;
    }

    private getSkipString(isQueryStringIndicatorAdded:boolean):string{
        if(!isQueryStringIndicatorAdded){
            return "?$skip=" + this.skipValue.toString();
        }else{
            return "&$skip=" + this.skipValue.toString();
        }
    }

    private getSelectString(isQueryStringIndicatorAdded:boolean):string{
        if(!isQueryStringIndicatorAdded){
            return "?$select=" + this.selectValue.toString();
        }else{
            return "&$select=" + this.selectValue.toString();
        }
    }

    private getTopString(isQueryStringIndicatorAdded:boolean):string{
        if(!isQueryStringIndicatorAdded){
            return "?$top=" + this.topValue.toString();
        }else{
            return "&$top=" + this.topValue.toString();
        }
    }

    private getOrderByString(isQueryStringIndicatorAdded:boolean):string{
        let orderByString = "$orderby=";
        
        for(let index = 0; index < this.orderBys.length; index++){
            let orderBy:ODataOrder = this.orderBys[index];

            if(index == 0){
                orderByString += orderBy.toString();
            }else{
                orderByString += "," + orderBy.toString();
            }
        }
        
        if(!isQueryStringIndicatorAdded){
            return "?" + orderByString;
        }else{
            return "&" + orderByString;
        }
    }

    //TODO:  Expand needs work.  Nested expands use a different syntax than other operations.
    //They look like $expand=Goal($expand=GoalCategory) instead of $expand=Goal/GoalCategory as 
    //is implemented now
    private getExpandString(isQueryStringIndicatorAdded:boolean):string{
        let expandString = "$expand=";
        
        for(let index = 0; index < this.expands.length; index++){
            let expand:ODataExpand = this.expands[index];

            if(index == 0){
                expandString += expand.toString();
            }else{
                expandString += "," + expand.toString();
            }
        }
        
        if(!isQueryStringIndicatorAdded){
            return "?" + expandString;
        }else{
            return "&" + expandString;
        }
    }

    private getFilterString(isQueryStringIndicatorAdded:boolean):string{
        let filterString = "$filter=";
        
        for(let index = 0; index < this.filters.length; index++){
            let filter:ODataFilter = this.filters[index];

            if(index == 0){
                filterString += filter.toString();
            }else{
                filterString += " and " + filter.toString();
            }
        }
        
        if(!isQueryStringIndicatorAdded){
            return "?" + filterString;
        }else{
            return "&" + filterString;
        }
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/ng-router-loader?{"loader":"async-system","genDir":"compiled","aot":false}!./src/app/utility/odata/odata-builder.ts